import * as React from "react";
import { useState, Suspense } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import Footer from "../components/Footer"

import { h3, body, bodyLarge } from "../commonStyles/Fonts";

// TODO animacja submitowania/ostylować błędy logowania
import { ReactComponent as GoogleIcon } from "../assets/icons/google.svg?react";
import { ReactComponent as FacebookIcon } from "../assets/icons/facebook.svg?react";
import { ReactComponent as AppleIcon } from "../assets/icons/apple.svg?react";
import { ReactComponent as PasswordVisibleIcon } from "../assets/icons/password-eye-visible.svg?react";
import { ReactComponent as PasswordHiddenIcon } from "../assets/icons/password-eye-hidden.svg?react";

import { styled } from "../../stitches.config";
import Link from "../../components/Link";

import { useAuth } from "../../context/AuthContext";
import { loginUser } from "../../api/account";
import { useQuery } from "../pages/SearchPage";

type LoginFormValues = {
  email: string;
  password: string;
};
const LoginForm: React.FC = () => {
  const [, dispatch] = useAuth();
  // TODO brać location z propsów - trzeba znaleźć odpowiedni interfejs - w typach RouteProps/RouteComponentProps coś jest namieszane
  const location = useLocation();
  const navigate = useNavigate();
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const query = useQuery();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const initialValues: LoginFormValues = {
    email: query.get("email") || "",
    password: "",
  };
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required("Pole wymagane")
      .email("Wprowadź poprawny adres e-mail"),
    password: Yup.string().required("Pole wymagane"),
  });

  const handleSubmit = ({ email, password }: LoginFormValues, { setErrors }: { setErrors: any }) => {
    const { from } = (location.state as any) || { from: { pathname: "/" } };
    loginUser({ username: email, password })
      .then((_res) => {
        dispatch({ type: "login" });
        navigate(from, { replace: true });
      })
      .catch((err) => {
        console.log(err);
        setErrors(err.errors)
        // dispatch({ type: "set_error", payload: err });
      });
  };

  const togglePasswordVisible = () => setPasswordVisible((prev) => !prev);

  return (
    <>
      <Overlay>
        <FormWrapper>
          <FormTitle>
            <p className={h3()}>Zaloguj się</p>
          </FormTitle>
          <FormWrapperButtons>
            <a href="/security/auth?authclient=facebook"><FacebookIcon /></a>
            <a href="/security/auth?authclient=google"><GoogleIcon /></a>
            {/*
          <button>
            <AppleIcon />
          </button>
          */}
          </FormWrapperButtons>

          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form className={body()}>
                lub
                <label
                  className={`${errors.email && touched.email && "show-error-input-text"
                    }`}
                  htmlFor="email"
                >
                  Email
                </label>
                <Field
                  type="text"
                  name="email"
                  className={`${bodyLarge()} ${errors.email && touched.email && "show-error-input-border"
                    }`}
                  placeholder="Wpisz swój email"
                />
                {errors.email && touched.email && (
                  <span className="validationError">{errors.email}</span>
                )}
                <label
                  className={`${errors.password && touched.password && "show-error-input-text"
                    }`}
                  htmlFor="password"
                >
                  Hasło
                </label>
                <PasswordWrapper>
                  <Field
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    className={`${bodyLarge()} ${errors.password &&
                      touched.password &&
                      "show-error-input-border"
                      }`}
                    placeholder="Wpisz swoje hasło"
                  />
                  <Suspense fallback={null}>
                    <PasswordIcon onClick={togglePasswordVisible}>
                      {isPasswordVisible ? (
                        <PasswordVisibleIcon />
                      ) : (
                        <PasswordHiddenIcon />
                      )}
                    </PasswordIcon>
                  </Suspense>
                </PasswordWrapper>
                {errors.password && touched.password && (
                  <span className="validationError">{errors.password}</span>
                )}
                <Link to="/resetPassword">
                  <span className={`${body()} passwordRecovery`}>Nie pamiętam hasła</span>
                </Link>
                <Field
                  className={bodyLarge()}
                  type="submit"
                  value="Zaloguj się"
                />
              </Form>
            )}
          </Formik>
          <FormWrapperLink className={bodyLarge()}>
            <Link to="/register">
              Nie masz konta? <span>Zarejestruj się</span>
            </Link>
          </FormWrapperLink>
        </FormWrapper>
      </Overlay>
      <Footer />
    </>
  );
};

const Overlay = styled("div", {
  position: "relative",
  backgroundColor: "white",
  width: "100%",
  marginLeft: "auto",
  marginRight: "auto",
  minHeight: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  background: "$grayscale10",
  "@bp2": {
    padding: "72px 0 80px 0",
  },
});

const FormTitle = styled("div", {
  "& p": {
    width: "332px",
    margin: 0,
  },
});

const FormWrapper = styled("div", {
  padding: "32px 16px 32px 16px",
  color: "$grayscale100",
  background: "$grayscale0",
  "@bp2": {
    width: "auto",
    padding: "36px 40px 32px 40px",
  },
  "& form": {
    display: "flex",
    flexDirection: "column",
    "& label": {
      margin: "24px 0 4px 0",
    },
    "& input": {
      border: "1px solid $grayscale25",
      fontWeight: "500",
      padding: "12px 14px",
      transition: "0.2s",
      caretColor: "$primary",
      color: "$grayscale100Fixed",
      "&::placeholder": {
        color: "$grayscale50",
      },
      "&:hover": {
        border: "1px solid $grayscale50",
      },
      "&:focus": {
        border: "1px solid $grayscale50",
      },
    },
    "& input[type = submit]": {
      background: "$primary",
      borderWidth: "0px",
      color: "$grayscale0",
      margin: "24px 0",
    },
    "& a": {
      marginTop: "6px",
      "& span": {
        fontSize: "12px",
        lineHeight: "16px",
        color: "$grayscale90",
        textDecoration: "underline",
      },
      "& .passwordRecovery": {
        fontWeight: 400,
        letterSpacing: "0.1px",
      },
    },
  },
  "& .validationError": {
    color: "$aszdziennikPrimary",
    fontSize: "12px",
    lineHeight: "16px",
    marginTop: "8px",
  },

  ".show-error-input-border": {
    border: "1px solid $aszdziennikPrimary",
    "&:hover": {
      border: "1px solid $aszdziennikPrimary",
    },
    "&:focus": {
      border: "1px solid $aszdziennikPrimary",
    },
  },

  ".show-error-input-text": {
    color: "$aszdziennikPrimary",
  },
});

const FormWrapperLink = styled("div", {
  width: "100%",
  display: "flex",
  "& a": {
    margin: "0 auto",
    color: "$grayscale100",
    "& span": {
      color: "$primary",
    },
    "&:hover": {
      textDecoration: "none",
    },
  },
});

const FormWrapperButtons = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  margin: "24px 0",
  "& a": {
    boxSizing: "border-box",
    width: "48%",
    height: "40px",
    background: "none",
    border: "1.5px solid $grayscale25",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "@bp4": {
      height: "48px",
    },
    "& svg": {
      width: "18px",
      height: "18px",
      "@bp4": {
        width: "20px",
        height: "20px",
      },
    },
  },
});

const PasswordWrapper = styled("div", {
  width: "100%",
  position: "relative",
  input: {
    width: "100%",
  },
});

const PasswordIcon = styled("div", {
  position: "absolute",
  top: "16px",
  right: "16px",
  cursor: "pointer",
  userSelect: "none",
});

export default LoginForm;
